import { useRef, useState, useEffect } from 'react';

import { AppRoutes } from 'config';
import { useNavigate } from 'hooks';
import { Icon } from 'components/icon';
import { Popup } from 'components/popup';
import { Button } from 'components/button';
import { Feature, ButtonColors } from 'types';
import { useAppDispatch, useAppSelector } from 'store';
import { selectAuthUserFeatures } from 'store/slices/auth';
import { useManagerCompletionAlertsQuery } from 'store/api/cart';
import { usePersonalNotificationsQuery } from 'store/api/notifications';
import { TIME_OFF_POLL_MS, useTimeOffSettingsQuery } from 'store/api/time-off';
import {
  fetchNotifications,
  notificationsStore,
} from 'store/slices/notifications';

import * as Styles from './styles';
import { NotificationCard } from './card';
import { NotificationHeader } from './header';
import { PersonalNotificationCard } from './personal-card';
import { TimeOffWaitingCard } from './time-off-waiting-card';
import { CompletionAlertCard } from './completion-alert-card';

// PAYOUT SENT (owner, 26 Oct 2026): one call returns the person's own rows
// and unread count; polled only while the tab is in front.
const PERSONAL_POLL_MS = 60000;

// RTK Query's focus flags need setupListeners, which this app does not call:
// the page's own visibility drives the personal poll instead.
const usePageVisible = () => {
  const [visible, setVisible] = useState(
    () => document.visibilityState !== 'hidden'
  );
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
};

export const NotificationsPopup = ({
  generalNotifications = true,
}: {
  generalNotifications?: boolean;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, unreadCount, notifications } =
    useAppSelector(notificationsStore);

  const { data: alerts = [] } = useManagerCompletionAlertsQuery(undefined, {
    refetchOnFocus: true,
    pollingInterval: 30000,
  });
  // TIME OFF (plan §5; no switch since 25 Oct): roles with the `time_off`
  // tick (owner 2 Nov 2026) poll the waiting count. Every other role skips
  // the query.
  const approverRole = Boolean(
    useAppSelector(selectAuthUserFeatures)?.includes(Feature.TIME_OFF)
  );
  const { data: timeOff } = useTimeOffSettingsQuery(undefined, {
    skip: !approverRole,
    refetchOnFocus: true,
    pollingInterval: TIME_OFF_POLL_MS,
  });
  const timeOffWaiting = timeOff?.isApprover ? timeOff.waitingCount : 0;
  // Polled only while the page is visible; read again when it comes back.
  const visible = usePageVisible();
  const { data: personal, refetch: refetchPersonal } =
    usePersonalNotificationsQuery(undefined, {
      pollingInterval: visible ? PERSONAL_POLL_MS : 0,
    });
  const wasVisible = useRef(visible);
  useEffect(() => {
    if (visible && !wasVisible.current) {
      refetchPersonal();
    }
    wasVisible.current = visible;
  }, [visible, refetchPersonal]);
  const personalRows = personal?.rows ?? [];
  const personalUnread = personal?.unread ?? 0;
  useEffect(() => {
    if (generalNotifications) {
      dispatch(fetchNotifications());
    }
  }, [generalNotifications, dispatch]);
  const visibleNotifications = generalNotifications ? notifications : [];
  if (
    !generalNotifications &&
    !alerts.length &&
    !timeOffWaiting &&
    !personalRows.length
  ) {
    return null;
  }

  return (
    <Popup
      removeAutofocus
      position={['bottom center', 'bottom left', 'bottom right']}
      trigger={
        <Styles.StyledButton
          padding="0"
          transparent
          icon={<Icon.Bell />}
          title="Notifications"
          colorType={ButtonColors.BlackWhite}
          disabled={
            generalNotifications &&
            isLoading &&
            !alerts.length &&
            !timeOffWaiting &&
            !personalRows.length
          }
          hasUnreadCount={
            !!alerts.length ||
            !!timeOffWaiting ||
            personalUnread > 0 ||
            (generalNotifications && !!unreadCount)
          }
        />
      }
    >
      {(close) =>
        alerts.length ||
        timeOffWaiting ||
        personalRows.length ||
        visibleNotifications?.length ? (
          <Styles.Container>
            {generalNotifications && (
              <NotificationHeader isDialog unreadCount={unreadCount} />
            )}
            <Styles.Content>
              {timeOffWaiting > 0 && (
                <TimeOffWaitingCard close={close} count={timeOffWaiting} />
              )}
              {alerts.map((alert) => (
                <CompletionAlertCard
                  alert={alert}
                  close={close}
                  key={alert.id}
                />
              ))}
              {personalRows.map((notification, i) => (
                <PersonalNotificationCard
                  close={close}
                  notification={notification}
                  key={`personal-${notification.id}`}
                  showDivider={i > 0 || alerts.length > 0 || timeOffWaiting > 0}
                />
              ))}
              {visibleNotifications?.map((notification, i) => (
                <NotificationCard
                  isDialog
                  close={close}
                  key={notification.id}
                  notification={notification}
                  showDivider={
                    i > 0 ||
                    alerts.length > 0 ||
                    timeOffWaiting > 0 ||
                    personalRows.length > 0
                  }
                />
              ))}
            </Styles.Content>

            {generalNotifications && (
              <Button
                transparent
                padding="16px"
                text="View all"
                colorType={ButtonColors.LightBlue}
                onClick={() => {
                  close();
                  navigate(AppRoutes.Notifications.Home);
                }}
              />
            )}
          </Styles.Container>
        ) : (
          <Styles.StyledPlaceholder text="No notifications" />
        )
      }
    </Popup>
  );
};

export { NotificationCard, NotificationHeader };
